import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import { ref, push } from "firebase/database";
import {
  Star, Users, Gift, TrendingUp, CheckCircle, AlertCircle,
  GraduationCap, Phone, Mail, Building2, ArrowLeft
} from "lucide-react";

const PERKS = [
  { icon: Star, color: "bg-yellow-100 text-yellow-600", title: "Verified Badge", desc: "Get an instant verified badge on your profile" },
  { icon: Gift, color: "bg-green-100 text-green-600", title: "Free Premium", desc: "Priority listing placement for 3 months" },
  { icon: Users, color: "bg-blue-100 text-blue-600", title: "Build Network", desc: "Connect with ambassadors from 20+ colleges" },
  { icon: TrendingUp, color: "bg-purple-100 text-purple-600", title: "Resume Boost", desc: "Official CampusSwap Ambassador certificate" },
];

const RESPONSIBILITIES = [
  "Spread the word about CampusSwap in your college",
  "Help students sign up and post their first listing",
  "Share listings in college WhatsApp groups",
  "Provide feedback to improve the app",
  "Organize a campus drive (optional)",
];

export default function Ambassador() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: userProfile?.name || "",
    college: userProfile?.college || "",
    phone: userProfile?.phone || "",
    email: userProfile?.email || "",
    year: "",
    branch: "",
    whyMe: "",
    followers: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  function handleChange(e) { setForm({ ...form, [e.target.name]: e.target.value }); }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!form.year) return setError("Please select your year of study.");
    if (!form.branch.trim()) return setError("Please enter your branch/stream.");
    if (form.whyMe.trim().length < 30) return setError("Please write at least 30 characters about why you'd be a great ambassador.");

    try {
      setLoading(true);
      await push(ref(db, "ambassadors"), {
        ...form,
        userId: currentUser?.uid || "",
        status: "pending",
        appliedAt: new Date().toISOString(),
      });
      setDone(true);
    } catch (err) {
      setError("Failed to submit. Please try again.");
    } finally { setLoading(false); }
  }

  if (done) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6 pb-24">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-sm w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={40} className="text-green-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Application Submitted!</h2>
          <p className="text-gray-500 text-sm mb-4">
            We'll review your application and reach out within <strong>48 hours</strong> on WhatsApp.
          </p>
          <div className="bg-blue-50 rounded-xl p-4 text-left">
            <p className="text-xs text-blue-700 font-semibold mb-2">What happens next?</p>
            <ul className="text-xs text-blue-600 space-y-1">
              <li>✅ Team reviews your application</li>
              <li>✅ WhatsApp call for quick intro</li>
              <li>✅ Get your ambassador kit & verified badge</li>
              <li>✅ Start representing CampusSwap at {form.college}</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-600 px-4 pt-6 pb-14 text-white">
        <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-full hover:bg-white/20 mb-2"><ArrowLeft size={20} /></button>
        <div className="text-4xl mb-3 text-center">🎓</div>
        <h1 className="text-2xl font-bold text-center mb-1">Campus Ambassador Program</h1>
        <p className="text-blue-200 text-sm text-center">Represent CampusSwap at your college</p>
      </div>

      <div className="px-4 -mt-8 space-y-4">
        {/* Stats */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <div className="grid grid-cols-3 divide-x divide-gray-100 text-center">
            <div className="pr-3">
              <p className="text-2xl font-bold text-blue-600">20+</p>
              <p className="text-xs text-gray-500 mt-0.5">Colleges</p>
            </div>
            <div className="px-3">
              <p className="text-2xl font-bold text-green-600">500+</p>
              <p className="text-xs text-gray-500 mt-0.5">Students</p>
            </div>
            <div className="pl-3">
              <p className="text-2xl font-bold text-purple-600">₹0</p>
              <p className="text-xs text-gray-500 mt-0.5">Cost to Join</p>
            </div>
          </div>
        </div>

        {/* Perks */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <h2 className="font-bold text-gray-800 mb-3">🎁 What you get</h2>
          <div className="grid grid-cols-2 gap-3">
            {PERKS.map(({ icon: Icon, color, title, desc }) => (
              <div key={title} className={`rounded-xl p-3 ${color.split(" ")[0]}/10 border border-gray-100`}>
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${color}`}>
                  <Icon size={16} />
                </div>
                <p className="font-semibold text-gray-800 text-xs">{title}</p>
                <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Responsibilities */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <h2 className="font-bold text-gray-800 mb-3">📋 What you do</h2>
          <div className="space-y-2">
            {RESPONSIBILITIES.map((r, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</div>
                <p className="text-sm text-gray-700">{r}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Application Form */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <h2 className="font-bold text-gray-800 mb-1">📝 Apply Now</h2>
          <p className="text-xs text-gray-500 mb-4">Takes 2 minutes · We respond in 48 hours</p>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-4 text-sm">
              <AlertCircle size={16} className="flex-shrink-0" />{error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="relative">
              <Users size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input name="name" value={form.name} onChange={handleChange} placeholder="Full Name" required
                className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
            <div className="relative">
              <Building2 size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input name="college" value={form.college} onChange={handleChange} placeholder="College Name" required
                className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <select name="year" value={form.year} onChange={handleChange} required
                  className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700">
                  <option value="">Year of Study</option>
                  <option>1st Year</option>
                  <option>2nd Year</option>
                  <option>3rd Year</option>
                  <option>4th Year</option>
                  <option>5th Year</option>
                  <option>PG / Masters</option>
                </select>
              </div>
              <div className="relative">
                <GraduationCap size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input name="branch" value={form.branch} onChange={handleChange} placeholder="Branch/Stream" required
                  className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            </div>
            <div className="relative">
              <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="WhatsApp Number" required maxLength={10}
                className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
            <div className="relative">
              <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email Address" required
                className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Instagram/LinkedIn followers (optional)</label>
              <input name="followers" value={form.followers} onChange={handleChange} placeholder="e.g. 500 Instagram followers"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Why would you be a great campus ambassador? *</label>
              <textarea name="whyMe" value={form.whyMe} onChange={handleChange} rows={4}
                placeholder="Tell us about yourself, your influence in college, how you'll spread CampusSwap..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" />
              <p className="text-xs text-gray-400 mt-0.5">{form.whyMe.length}/30 minimum characters</p>
            </div>
            <button type="submit" disabled={loading}
              className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-bold hover:bg-blue-700 disabled:opacity-50">
              {loading ? "Submitting..." : "Submit Application →"}
            </button>
          </form>
        </div>

        {/* FAQ */}
        <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100">
          <p className="font-semibold text-blue-800 text-sm mb-2">💬 Common Questions</p>
          <div className="space-y-2 text-xs text-blue-700">
            <p><strong>Q: Is there any target or quota?</strong><br />No fixed quota. Do what you can — even 5 signups from your college helps!</p>
            <p><strong>Q: Can I resign anytime?</strong><br />Yes, completely voluntary. No commitments.</p>
            <p><strong>Q: Do I get paid?</strong><br />Not cash, but you get perks, recognition, and a growing network.</p>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 pb-2">Questions? WhatsApp us or email campusswap@gmail.com</p>
      </div>
    </div>
  );
}
